/**
 * Watches an oauth_sessions row by polling and reports when the desktop app
 * has redeemed the authorization code (auth_code_used flips to true). Used
 * by DesktopAuthFlow to flip the "Redirecting…" view to "Connected ✓" —
 * confirmation that the parallel-poll or deep-link redemption landed on the
 * server.
 *
 * Polls instead of subscribing via Realtime postgres_changes: that path
 * decodes every WAL change fleet-wide to serve the handful of browsers on
 * this page, and was measured at 56%+ of total Postgres query CPU. RLS on
 * oauth_sessions still restricts SELECT to auth.uid() = user_id, so only the
 * signed-in owner of the session can read its redemption state.
 */

import { useEffect, useState } from "react";
import { useSupabase } from "../contexts/SupabaseContext";

const POLL_INTERVAL_MS = 1500;

export function useOAuthConsumptionWatcher(sessionId: string | null) {
    const { supabaseClient } = useSupabase();
    const [consumed, setConsumed] = useState(false);

    useEffect(() => {
        if (!supabaseClient || !sessionId) {
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const poll = async () => {
            const { data } = await supabaseClient.from("oauth_sessions").select("auth_code_used").eq("id", sessionId).maybeSingle();
            if (cancelled) {
                return;
            }
            if (data?.auth_code_used) {
                setConsumed(true);
                return;
            }
            timer = setTimeout(poll, POLL_INTERVAL_MS);
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [
        supabaseClient,
        sessionId,
    ]);

    return consumed;
}
